<script setup lang="ts">
import { CardProps } from "./Card.props"
import { iconsSrcMap } from "./Card.iconMapping"

const props = withDefaults(defineProps<CardProps>(), {
  theme: "green"
})

const { sendEventPhoneHelp } = useGA4SiteEvents()
const route = useRoute()
const isLogged = useIsLogged()

const emit =
  defineEmits<{ (e: "onClose", iconType: CardProps["iconType"]): void }>()

const mustNavigateToOrders = computed(
  () =>
    isLogged.value &&
    (props.iconType === "customerServiceOrder" ||
      props.iconType === "customerServiceReceipt")
)

const handleClickCard = () => {
  emit("onClose", props.iconType)

  if (mustNavigateToOrders) {
    navigateTo(customPaths.profile.orders)
    return
  }

  if (props.iconType == "customerServiceCall") {
    sendEventPhoneHelp()
  }

  // navigateTo() is needed to make sure that query params are updated even if the cta is clicked when already inside the '/contatti' path
  if (route.fullPath.startsWith(customPaths.contactUs)) {
    if (props.iconType === "customerServiceChat") {
      navigateTo(customPaths.contactUs)
    }
    if (props.iconType === "customerServiceMail") {
      navigateTo(
        `${customPaths.contactUs}?help_subject=${helpSubjectParams.consulting_pharma}`
      )
    }
  }
}
</script>

<template>
  <div
    class="banner-help-utility-help_card flex flex-col rounded-lg"
    :class="{ 'bg-white p-4': ['green', 'blue'].includes(theme) }"
  >
    <!-- Green Bar -->
    <div
      class="flex items-center gap-2 rounded"
      :class="{
        'bg-tiffany-10 p-4': theme === 'green',
        'bg-cobalt-50 p-4': theme === 'blue'
      }"
    >
      <UtilsIcon
        v-if="iconType"
        v-bind="iconsSrcMap[iconType]"
        :class="['green', 'blue'].includes(theme) ? 'h-8 w-8' : 'h-12 w-12'"
        class="shrink-0"
      />
      <p
        class="pig-bold"
        :class="{
          'text-tiffany-400':
            theme === 'green' || !['green', 'blue'].includes(theme),
          'text-default-blue-main': theme === 'blue'
        }"
      >
        {{ title }}
      </p>
    </div>

    <!-- Description -->
    <div class="flex h-full flex-col justify-between">
      <div>
        <UtilsMarkdown
          v-if="text"
          :class="['green', 'blue'].includes(theme) ? 'py-4' : 'py-2'"
          class="
            beaver
            markdown__link-underlined
            markdown__link-green-main
            markdown__strong-black-80
            markdown__link-hover-green-300
          "
          :content="text"
        />
      </div>

      <UtilsButton
        @click="handleClickCard"
        v-if="isLogged && hrefLoggedContact"
        class="
          contact
          custom-btn-underlined
          custom-btn-no-padding
          custom-btn-pig-medium
          custom-btn-whitespace
          custom-btn-start
          custom-btn-hover-green-300
          text-green-main
          cursor-pointer
          underline
        "
        :path="hrefLoggedContact"
        :text="contact"
        theme="ghost-green"
        forceSelf
      />
      <UtilsButton
        @click="handleClickCard"
        v-else-if="hrefContact"
        class="
          contact
          custom-btn-underlined
          custom-btn-no-padding
          custom-btn-pig-medium
          custom-btn-whitespace
          custom-btn-start
          custom-btn-hover-green-300
          text-green-main
          cursor-pointer
          underline
        "
        :path="!mustNavigateToOrders ? hrefContact : ''"
        :text="contact"
        theme="ghost-green"
        forceSelf
      />
    </div>
  </div>
</template>
